import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import { Add, AttachFile, Delete, Edit, GridView, List } from "@mui/icons-material";
import { Box, Button, ButtonGroup, Divider, IconButton, ToggleButtonGroup } from "@mui/material";

import analytics from "fond/analytics";
import { AttachmentModal } from "fond/attachments";
import { DeleteAttachmentModal } from "fond/attachments/DeleteAttachmentModal";
import { useGetAttachmentRows } from "fond/attachments/hooks/useGetAttachmentRows";
import { RenameAttachmentModal } from "fond/attachments/RenameAttachmentModal";
import { AttachmentEventLocation } from "fond/attachments/types";
import { Attachment, AttachmentEntityType, Project, Store } from "fond/types";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { FilesViewer, NonIdealState } from "fond/widgets";
import { ViewMode } from "fond/widgets/FilesViewer/types";
import { ModalCloseHandler } from "fond/widgets/Modal";

import { ToggleButton } from "./propertiesPanel.styles";

interface IProps {
  project: Project;
}

const FeatureAttachments: React.FC<IProps> = ({ project }) => {
  const canUpload = permissionCheck(project.Permission.Level, Actions.FEATURE_UPLOAD_ATTACHMENT);
  const canRename = permissionCheck(project.Permission.Level, Actions.FEATURE_EDIT_ATTACHMENT);
  const canDelete = permissionCheck(project.Permission.Level, Actions.FEATURE_DELETE_ATTACHMENT);
  const { selectedFeature } = useSelector((state: Store) => state.project);
  const featureId = (selectedFeature.featureId as string) || selectedFeature.featureProperties.id;

  const [getRows, attachments, totalFiles] = useGetAttachmentRows(project.ID, featureId);

  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [renameAttachment, setRenameAttachment] = useState<Attachment | null>(null);
  const [deleteAttachments, setDeleteAttachments] = useState<Attachment[] | null>(null);
  const [view, setView] = useState<ViewMode>("list");
  const [selected, setSelected] = useState<Attachment[]>([]);
  const [totalAttachments, setTotalAttachments] = useState<number>(0);

  // Use update effect to set total files when the total or attachmants changes and ignore initial render
  // as it will be the value of the previous dataset.
  useUpdateEffect(() => {
    setTotalAttachments(totalFiles);
  }, [totalFiles, attachments]);

  const baseAnalyticsProps = useMemo((): {
    context: AttachmentEntityType;
    location: AttachmentEventLocation;
    featureId?: string;
    projectId: string;
  } => {
    return {
      context: "Feature",
      location: "panel",
      featureId,
      projectId: project.ID,
    };
  }, [featureId, project.ID]);
  const hasAttachments = useMemo(() => totalAttachments > 0, [totalAttachments]);
  const handleCloseDeleteAttachments: ModalCloseHandler = (_, reason) => {
    const analyticsProps = {
      ...baseAnalyticsProps,
      totalFiles: totalAttachments,
      count: deleteAttachments?.length,
    };
    if (reason === "complete") {
      analytics.track("attachments.files.deleted", analyticsProps);
      setSelected([]);
    } else {
      analytics.track("attachments.files.delete.cancelled", analyticsProps);
    }
    setDeleteAttachments(null);
  };

  const handleCloseRenameAttachments: ModalCloseHandler<string> = (_, reason, name) => {
    if (reason === "complete") {
      analytics.track("attachments.file.renamed", { ...baseAnalyticsProps, changed: renameAttachment?.Name !== name });
    } else {
      analytics.track("attachments.file.rename.cancelled", baseAnalyticsProps);
    }
    setRenameAttachment(null);
  };

  const handleRenameClicked = () => {
    setRenameAttachment(selected[0]);
    analytics.track("attachments.action.rename.clicked", baseAnalyticsProps);
  };

  const handleDeleteClicked = () => {
    setDeleteAttachments(selected);
    analytics.track("attachments.action.delete.clicked", {
      ...baseAnalyticsProps,
      totalFiles: totalAttachments,
      count: selected.length,
    });
  };
  return (
    <Box
      data-testid="feature-attachments-tab-content"
      flex={1}
      display="flex"
      flexDirection="column"
      overflow="hidden auto"
      className="customScrollbars"
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" p={1.5}>
        <Button
          disabled={!canUpload}
          onClick={() => setShowAttachmentModal(true)}
          variant="contained"
          color="primary"
          startIcon={!hasAttachments && <Add />}
        >
          {hasAttachments ? "Manage files" : "Upload a file"}
        </Button>
        <ToggleButtonGroup
          data-testid="view-mode-toggle-buttons"
          value={view}
          onChange={(_, newView: ViewMode) => {
            if (newView) {
              setView(newView);
              analytics.track("attachments.view.changed", { ...baseAnalyticsProps, view: newView });
            }
          }}
          exclusive
          size="small"
        >
          <ToggleButton value="gallery" aria-label="Gallery">
            <GridView fontSize="small" />
          </ToggleButton>

          <ToggleButton value="list" aria-label="List">
            <List fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Divider />
      <Box flex={1}>
        <FilesViewer
          files={attachments ?? []}
          total={totalAttachments}
          view={view}
          getRows={getRows}
          selected={selected}
          onSelectionChange={(newSelection) => setSelected(newSelection as Attachment[])}
          listViewPagination={false}
          actions={
            <ButtonGroup>
              <IconButton
                title="Rename selected file"
                size="small"
                color="primary"
                disabled={selected.length !== 1 || !canRename}
                onClick={handleRenameClicked}
              >
                <Edit />
              </IconButton>
              <IconButton
                title="Delete selected file(s)"
                size="small"
                color="primary"
                disabled={selected.length === 0 || !canDelete}
                onClick={handleDeleteClicked}
              >
                <Delete />
              </IconButton>
              {/*
                  * TODO: Add appropriate functionality once UI/UX for this feature is available
                  <IconButton size="small" disabled={selected.length !== 1}>
                    <InfoOutlined />
                  </IconButton>
                */}
            </ButtonGroup>
          }
          trackingContext="feature-attachments"
        />
      </Box>
      {attachments && attachments.length === 0 && <NonIdealState icon={<AttachFile />} title="" description="Selected feature has no attachments" />}
      {showAttachmentModal && (
        <AttachmentModal
          isOpen
          onClose={() => setShowAttachmentModal(false)}
          attachmentEntityType="Feature"
          initialView={!hasAttachments ? "staging" : "list"}
        />
      )}
      {renameAttachment != null && (
        <RenameAttachmentModal attachment={renameAttachment} items={attachments} onClose={handleCloseRenameAttachments} validateUniqueName />
      )}
      {deleteAttachments != null && <DeleteAttachmentModal attachments={deleteAttachments} onClose={handleCloseDeleteAttachments} />}
    </Box>
  );
};

export default FeatureAttachments;
